import { WarningIcon } from '@plato/ui-legacy/assets/icons/warning-icon';
import { BaseConfirmationDialog } from '@plato/ui-legacy/components/dialog/confirmation-dialog';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import type { DialogInnerProps } from '@plato/ui-legacy/components/dialog/types';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';

type NoteDeleteDialogProps = {
    noteId: string;
    onDeleted?: () => void;
} & DialogInnerProps;

export const NoteDeleteDialog = DialogStateManager.create<NoteDeleteDialogProps>(({ noteId, onDeleted }) => {
    const { remove } = useDialog();
    const { t } = useTranslation();
    const { toast } = useToast();
    const apiUtils = api.useUtils();

    const deleteMutation = api.platoUser.command.notes.delete.useMutation({
        onSuccess: () => {
            remove();

            toast({
                icon: 'success',
                title: t('notes__delete_confirmation__toast__success_title', { ns: 'notes' }),
                description: t('notes__delete_confirmation__toast__success_description', {
                    ns: 'notes',
                }),
            });

            void Promise.all([
                apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate(),
                apiUtils.platoUser.query.customerActivity.getFeedList.getPinnedNotes.invalidate(),
            ]);

            onDeleted?.();
        },
        onError: (error) => {
            toast({
                icon: 'warning',
                title: 'Error',
                description: error.message,
            });
        },
    });

    const handleDelete = () => {
        deleteMutation.mutate({ noteId });
    };

    return (
        <BaseConfirmationDialog
            focus="destroy"
            icon={WarningIcon}
            title={t('notes__delete_confirmation__title', { ns: 'notes' })}
            description={t('notes__delete_confirmation__description', { ns: 'notes' })}
        >
            <BaseConfirmationDialog.Actions>
                <BaseConfirmationDialog.RejectButton onClick={remove}>
                    {t('form__cancel')}
                </BaseConfirmationDialog.RejectButton>
                <BaseConfirmationDialog.AcceptButton
                    onClick={handleDelete}
                    isLoading={deleteMutation.isPending}
                >
                    {t('form__delete')}
                </BaseConfirmationDialog.AcceptButton>
            </BaseConfirmationDialog.Actions>
        </BaseConfirmationDialog>
    );
});
